import { AxiosError } from 'axios'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import * as yup from 'yup'
import { Sku, SkuThreshold, SkuThresholdTarget } from 'src/api/models'
import { useForm, MapSelectField, NumberField, QuerySelectField } from 'src/forms'
import { setApiErrors } from 'src/forms/errors'
import { skuit } from 'src/services/skuit'
import { handleErrorMessage, Notification } from 'src/components/Notification'
import { useQueryPaginator } from 'src/components/Table'

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}))

interface SkuThresholdProps {
  name: string
}

const skuThresholdSchema = yup.object().shape({
  target: yup.string().required().label('Target'),
  facility: yup.string().facility().optional().label('Facility'),
  min_count: yup.number().required().label('Minimum'),
  refill_count: yup.number().optional().label('Minimum'),
})

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: () => void
  update?: boolean
  skuThreshold?: SkuThreshold
  sku?: Sku
}

export const SkuThresholdDialog = (props: Props) => {
  const { open, handleClose, onSave, update, skuThreshold, sku } = props
  const classes = useStyles()
  const [target, setTarget] = useState<string>('')

  const skuPaginator = useQueryPaginator('sku-list', skuit.sc.skus.list)
  const facilityPaginator = useQueryPaginator('facility-list', skuit.sc.facilities.list)

  const onSubmit = (data: SkuThresholdProps) => {
    let func = skuit.sc.skus.thresholds.create
    let action = 'creat'
    if (update) {
      func = (data: SkuThresholdProps) => skuit.sc.skus.thresholds.patch(skuThreshold?.guid || '', data)
      action = 'updat'
    }
    func(data)
      .then((newSkuThreshold: SkuThreshold) => {
        handleClose()
        onSave?.()
        toast.success(() => (
          <Notification
            message={`Successfully ${action}ed SkuThreshold: ${newSkuThreshold.name}`}
            newObject={newSkuThreshold}
          />
        ))
      })
      .catch((error: AxiosError) => {
        handleErrorMessage(error, action, 'SkuThreshold')
        setApiErrors(formData.setError, error)
      })
  }

  const { Form, data: formData } = useForm(skuThresholdSchema)

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
        <DialogTitle>{update ? 'Update' : 'Create'} SKU Threshold Alert</DialogTitle>
        <DialogContent>
          <QuerySelectField
            name="sku"
            label="SKU"
            paginator={skuPaginator} 
            defaultValue={sku}
            disabled={!!sku || update}
          />
          <MapSelectField
            name="target"
            label="Target"
            options={SkuThreshold.Target}
            onSelected={(obj: SkuThresholdTarget) => setTarget(obj)}
          />
          <QuerySelectField
            name="facility"
            label="Facility"
            paginator={facilityPaginator} 
            disabled={target !== 'fac'}
          />
          <NumberField
            name="min_count" // eslint-disable-line camelcase
            label="Minimum"
          />
          <NumberField
            name="refill_count"
            label="Refill To"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained" autoFocus>
            Save
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
